import { useEffect, useMemo, useState } from "react";
import {
  buildMealPrepSourceRevision,
  buildMealPrepToggleValue,
  createMealPrepDraft,
  getMealPrepPublicationIssue,
  parseMealPrepOverride,
} from "../services/mealPrep";
import { resolveMealPrepEditorialState } from "../services/mealPrepEditorialState";
import Icon from "./Icon";

const card = {
  border: "1px solid rgba(212, 201, 168, 0.16)",
  borderRadius: 12,
  background: "rgba(255,255,255,0.025)",
  overflow: "hidden",
};

const input = {
  width: "100%",
  borderRadius: 7,
  border: "1px solid rgba(212, 201, 168, 0.18)",
  background: "rgba(0,0,0,0.16)",
  color: "#f0f0e8",
  fontFamily: "inherit",
  fontSize: ".84rem",
  padding: "8px 10px",
};

const smallButton = {
  borderRadius: 7,
  border: "1px solid rgba(212, 201, 168, 0.18)",
  background: "transparent",
  color: "#cfcfc4",
  fontFamily: "inherit",
  fontSize: ".74rem",
  padding: "6px 9px",
  cursor: "pointer",
};

function compactDraft(value) {
  return {
    version: 1,
    enabled: value.enabled === true,
    status: value.status === "validated" ? "validated" : "draft",
    source_revision: value.source_revision,
    sessions: value.sessions.map((session) => ({
      id: session.id,
      title: session.title.trim(),
      ...(Number.isInteger(session.estimated_minutes)
        ? { estimated_minutes: session.estimated_minutes }
        : {}),
      tasks: session.tasks.map((task) => ({
        id: task.id,
        label: task.label.trim(),
        ...(task.detail?.trim() ? { detail: task.detail.trim() } : {}),
        ...(Number.isInteger(task.estimated_minutes)
          ? { estimated_minutes: task.estimated_minutes }
          : {}),
        linked_meal_ids: Array.isArray(task.linked_meal_ids) ? task.linked_meal_ids : [],
        serves_days: Array.isArray(task.serves_days) ? task.serves_days : [],
      })),
    })),
  };
}

function move(items, from, to) {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function newId(prefix) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export default function MealPrepEditorialCard({
  weekMeals,
  value,
  onSave,
  onDirtyChange,
}) {
  const parsed = useMemo(() => parseMealPrepOverride(value), [value]);
  const currentRevision = useMemo(
    () => buildMealPrepSourceRevision(weekMeals),
    [weekMeals],
  );
  const [draft, setDraft] = useState(parsed);
  const [baseline, setBaseline] = useState(parsed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [lastLocalAction, setLastLocalAction] = useState(null);

  useEffect(() => {
    setDraft(parsed);
    setBaseline(parsed);
    setError(null);
    setLastLocalAction(null);
  }, [parsed]);

  const dirty = !!draft && JSON.stringify(compactDraft(draft)) !== JSON.stringify(baseline);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const publicationIssue = getMealPrepPublicationIssue(weekMeals, draft);
  const stale = !!draft?.enabled && draft.source_revision !== currentRevision;
  const totalTasks = draft?.sessions.reduce((sum, session) => sum + session.tasks.length, 0) || 0;
  const editorialState = resolveMealPrepEditorialState({
    stale,
    dirty,
    status: draft?.status,
    totalTasks,
    lastLocalAction,
  });

  async function persist(next) {
    const compact = compactDraft(next);
    const normalized = parseMealPrepOverride(compact);
    if (!normalized) {
      setError("Vérifie les titres et les durées avant d’enregistrer.");
      return false;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      if (typeof onSave !== "function") {
        throw new Error("Enregistrement indisponible dans cette étape.");
      }
      await onSave(compact);
      // La carte adopte uniquement la valeur dont la sauvegarde a réussi.
      // Elle ne dépend plus du délai de round-trip du parent pour refléter le
      // switch et éviter qu'un second clic reparte de l'ancienne valeur.
      setDraft(normalized);
      setBaseline(normalized);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2200);
      return true;
    } catch (err) {
      setError(err?.message || "Enregistrement impossible.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled() {
    if (await persist(buildMealPrepToggleValue(draft, weekMeals))) {
      setLastLocalAction(null);
    }
  }

  function prepareFromRecipes() {
    try {
      setDraft(createMealPrepDraft(weekMeals));
      setError(null);
      setLastLocalAction("refreshed");
    } catch (err) {
      setError(err?.message || "La proposition n’a pas pu être créée.");
    }
  }

  function updateSession(sessionIndex, patch) {
    setLastLocalAction("edited");
    setDraft((current) => ({
      ...current,
      status: "draft",
      source_revision: currentRevision,
      sessions: current.sessions.map((session, index) => (
        index === sessionIndex ? { ...session, ...patch } : session
      )),
    }));
  }

  function updateTask(sessionIndex, taskIndex, patch) {
    setLastLocalAction("edited");
    setDraft((current) => ({
      ...current,
      status: "draft",
      source_revision: currentRevision,
      sessions: current.sessions.map((session, index) => (
        index !== sessionIndex
          ? session
          : {
              ...session,
              tasks: session.tasks.map((task, indexTask) => (
                indexTask === taskIndex ? { ...task, ...patch } : task
              )),
            }
      )),
    }));
  }

  async function validateForApp() {
    if (!draft || dirty || stale || totalTasks === 0) return;
    if (await persist({ ...draft, status: "validated", source_revision: currentRevision })) {
      setLastLocalAction("validated");
    }
  }

  async function saveDraft() {
    if (await persist({ ...draft, status: "draft", source_revision: currentRevision })) {
      setLastLocalAction("saved");
    }
  }

  if (value && !parsed) {
    return (
      <section style={card} aria-labelledby="meal-prep-title">
        <div style={{ padding: 18 }}>
          <p style={{ margin: 0, color: "#e5c878", fontSize: ".8rem" }}>
            Meal prep à vérifier. Le contenu enregistré n’est pas conforme et ne sera pas publié.
          </p>
          <button
            type="button"
            onClick={() => persist({
              version: 1,
              enabled: false,
              status: "draft",
              source_revision: currentRevision,
              sessions: [],
            })}
            style={{ ...smallButton, marginTop: 12 }}
          >Réinitialiser</button>
        </div>
      </section>
    );
  }

  const enabled = draft?.enabled === true;

  return (
    <section style={card} aria-labelledby="meal-prep-title">
      <header style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "18px 18px 16px" }}>
        <Icon type="clipboard" size={18} style={{ color: "#d4c9a8", marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, color: "#8a8a7a", fontSize: ".66rem", fontWeight: 700, letterSpacing: ".13em", textTransform: "uppercase" }}>
            Organisation de la semaine
          </p>
          <h4 id="meal-prep-title" style={{ margin: "4px 0 0", color: "#f0f0e8", fontSize: "1rem" }}>
            Meal prep
          </h4>
          <p style={{ margin: "5px 0 0", color: "#99998d", fontSize: ".78rem", lineHeight: 1.55 }}>
            Une préparation pratique construite uniquement depuis les recettes déjà choisies. Aucune donnée cliente ou analyse n’est utilisée.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={toggleEnabled}
          disabled={saving || typeof onSave !== "function"}
          style={{
            width: 46,
            height: 26,
            border: `1px solid ${enabled ? "rgba(138,191,154,.55)" : "rgba(255,255,255,.18)"}`,
            borderRadius: 999,
            background: enabled ? "rgba(138,191,154,.22)" : "rgba(255,255,255,.04)",
            padding: 3,
            cursor: saving ? "wait" : "pointer",
            flexShrink: 0,
          }}
        >
          <span style={{
            display: "block",
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: enabled ? "#a8e0c0" : "#77776e",
            transform: enabled ? "translateX(18px)" : "translateX(0)",
            transition: "transform .18s ease",
          }} />
        </button>
      </header>

      {!enabled && error && (
        <p role="alert" style={{ margin: "0 18px 16px", color: "#f5c6c6", fontSize: ".76rem" }}>
          {error}
        </p>
      )}

      {enabled && (
        <div style={{ borderTop: "1px solid rgba(212,201,168,.10)", padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
            <div>
              <strong style={{ color: stale ? "#e5c878" : draft?.status === "validated" ? "#a8e0c0" : "#cfcfc4", fontSize: ".78rem" }}>
                {editorialState.label}
              </strong>
              {totalTasks > 0 && (
                <span style={{ marginLeft: 8, color: "#77776e", fontSize: ".74rem" }}>
                  {totalTasks} préparation{totalTasks > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={prepareFromRecipes}
              disabled={saving || editorialState.refreshDisabled}
              style={{
                ...smallButton,
                borderColor: stale || totalTasks === 0 ? "rgba(138,191,154,.32)" : "rgba(212,201,168,.15)",
                color: stale || totalTasks === 0 ? "#a8e0c0" : "#8a8a7a",
                cursor: saving ? "wait" : editorialState.refreshDisabled ? "default" : "pointer",
                opacity: editorialState.refreshDisabled ? .72 : 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Icon type={stale || totalTasks === 0 ? "refresh" : "check"} size={13} />
              {editorialState.refreshLabel}
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 7, margin: "0 0 14px" }}>
            {[
              { label: "1. Actualiser", done: totalTasks > 0 && !stale },
              { label: "2. Enregistrer", done: totalTasks > 0 && !stale && !dirty },
              { label: "3. Valider Anissa", done: draft?.status === "validated" && !stale && !dirty },
            ].map((step) => (
              <div
                key={step.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: 7,
                  padding: "7px 8px",
                  background: step.done ? "rgba(138,191,154,.08)" : "rgba(255,255,255,.025)",
                  border: `1px solid ${step.done ? "rgba(138,191,154,.22)" : "rgba(212,201,168,.10)"}`,
                  color: step.done ? "#a8e0c0" : "#77776e",
                  fontSize: ".68rem",
                  fontWeight: 650,
                }}
              >
                <Icon type={step.done ? "check" : "dot"} size={12} />
                <span>{step.label}</span>
              </div>
            ))}
          </div>

          <p style={{ margin: "-4px 0 5px", color: "#99998d", fontSize: ".72rem", lineHeight: 1.45 }}>
            {editorialState.nextStep}
          </p>
          <p style={{ margin: "0 0 14px", color: "#77776e", fontSize: ".7rem", lineHeight: 1.45 }}>
            La cliente choisira ensuite dans son application si elle répartit les préparations en une, deux ou trois sessions.
          </p>

          {draft?.sessions.map((session, sessionIndex) => (
            <div key={session.id} style={{ marginTop: sessionIndex ? 14 : 0, borderRadius: 10, background: "rgba(0,0,0,.12)", padding: 12 }}>
              {sessionIndex === 0 && (
                <p style={{ margin: "0 0 10px", color: "#99998d", fontSize: ".72rem", fontWeight: 700 }}>
                  {draft?.status === "validated" ? "Préparations validées par Anissa" : "Préparations proposées à Anissa"}
                </p>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {session.tasks.map((task, taskIndex) => (
                  <div key={task.id} style={{ borderLeft: "2px solid rgba(138,191,154,.22)", paddingLeft: 10 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <input
                        aria-label={`Étape ${taskIndex + 1} de ${session.title}`}
                        value={task.label}
                        maxLength={180}
                        onChange={(event) => updateTask(sessionIndex, taskIndex, { label: event.target.value })}
                        style={input}
                      />
                      <button type="button" aria-label="Monter l’étape" onClick={() => updateSession(sessionIndex, { tasks: move(session.tasks, taskIndex, taskIndex - 1) })} style={smallButton}>↑</button>
                      <button type="button" aria-label="Descendre l’étape" onClick={() => updateSession(sessionIndex, { tasks: move(session.tasks, taskIndex, taskIndex + 1) })} style={smallButton}>↓</button>
                      <button type="button" aria-label="Supprimer l’étape" onClick={() => updateSession(sessionIndex, { tasks: session.tasks.filter((_, index) => index !== taskIndex) })} style={{ ...smallButton, color: "#d9a6a6" }}>×</button>
                    </div>
                    <textarea
                      aria-label={`Détail de l’étape ${taskIndex + 1}`}
                      value={task.detail || ""}
                      maxLength={500}
                      rows={2}
                      onChange={(event) => updateTask(sessionIndex, taskIndex, { detail: event.target.value })}
                      style={{ ...input, marginTop: 6, resize: "vertical", lineHeight: 1.45 }}
                    />
                    {task.serves_days?.length > 0 && (
                      <p style={{ margin: "5px 0 0", color: "#77776e", fontSize: ".7rem" }}>
                        Prévu pour {task.serves_days.join(" · ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {session.tasks.length < 8 && (
                <button
                  type="button"
                  onClick={() => updateSession(sessionIndex, {
                    tasks: [...session.tasks, {
                      id: newId("prep-task"),
                      label: "Nouvelle préparation",
                      linked_meal_ids: [],
                      serves_days: [],
                    }],
                  })}
                  style={{ ...smallButton, marginTop: 10 }}
                >+ Ajouter une étape</button>
              )}
            </div>
          ))}

          {(error || publicationIssue || saved) && (
            <p role={error ? "alert" : "status"} style={{ margin: "12px 0 0", color: error ? "#f5c6c6" : publicationIssue ? "#e5c878" : "#8abf9a", fontSize: ".76rem", display: "flex", alignItems: "center", gap: 6 }}>
              <Icon type={error ? "warning" : saved ? "check" : "info"} size={13} />
              <span>{error || (saved ? (lastLocalAction === "validated" ? "Validation enregistrée" : "Brouillon enregistré") : publicationIssue)}</span>
            </p>
          )}

          {draft && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setDraft(baseline)}
                disabled={!dirty || saving}
                style={{ ...smallButton, opacity: !dirty ? .45 : 1 }}
              >Annuler les modifications</button>
              <button
                type="button"
                onClick={saveDraft}
                disabled={!dirty || saving}
                style={{ ...smallButton, color: "#d4c9a8", opacity: !dirty ? .45 : 1 }}
              >{saving ? "Enregistrement…" : "2. Enregistrer le brouillon"}</button>
              <button
                type="button"
                onClick={validateForApp}
                disabled={dirty || stale || totalTasks === 0 || saving || draft?.status === "validated"}
                style={{
                  ...smallButton,
                  borderColor: "rgba(138,191,154,.38)",
                  background: "rgba(138,191,154,.12)",
                  color: "#a8e0c0",
                  opacity: (dirty || stale || totalTasks === 0 || draft?.status === "validated") ? .45 : 1,
                }}
              >
                {draft?.status === "validated" ? "Validé par Anissa" : "3. Valider pour l’application"}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
